import { app } from '../app';
import { focus } from '../ui/compose';
// Keyboard and mouse for the game screen: held keys, keys pressed this frame, clicks on the world (not on windows).
export class Input {
  private readonly held = new Set<string>();
  private readonly pressedNow = new Set<string>();
  mouseX = 0;
  mouseY = 0;
  leftClicked = false;
  rightClicked = false;
  /** The right button went down on the world (not the UI) and is still down: blocking. */
  rightHeld = false;
  wheel = 0;
  shift = false;
  private readonly off: (() => void)[] = [];
  /** When a finger last went down or up: browsers follow taps with mouse events, which are the touch controls' not ours. */
  private lastTouch = -Infinity;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const on = <K extends keyof WindowEventMap>(target: Window | HTMLElement, type: K, fn: (e: WindowEventMap[K]) => void, opts?: AddEventListenerOptions) => {
      target.addEventListener(type, fn as EventListener, opts);
      this.off.push(() => target.removeEventListener(type, fn as EventListener, opts));
    };
    on(window, 'keydown', e => {
      this.shift = e.shiftKey;
      if (this.typing()) return;
      if (!e.repeat) this.pressedNow.add(e.code);
      this.held.add(e.code);
      if (['Tab', 'Space', 'F3', 'F5', 'Slash', 'Quote', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.code)) e.preventDefault();
    });
    on(window, 'keyup', e => {
      this.shift = e.shiftKey;
      this.held.delete(e.code);
    });
    on(window, 'blur', () => { this.held.clear(); this.rightHeld = false; });
    const touch = (e: PointerEvent) => { if (e.pointerType !== 'mouse') this.lastTouch = performance.now(); };
    on(window, 'pointerdown', touch, { capture: true });
    on(window, 'pointerup', touch, { capture: true });
    on(window, 'mouseup', e => { if (e.button === 2) this.rightHeld = false; });
    on(window, 'mousemove', e => {
      if (this.fromTouch()) return;
      this.mouseX = e.clientX;
      this.mouseY = e.clientY;
      this.shift = e.shiftKey;
    });
    // Only clicks that land on the game canvas (not on the UI, nor taken by it) count as world clicks.
    on(canvas, 'mousedown', e => {
      if (app.ui.pressTaken || this.fromTouch()) return;
      if (e.button === 0) this.leftClicked = true;
      if (e.button === 2) this.rightClicked = this.rightHeld = true;
      focus.set(null);
    });
    on(canvas, 'contextmenu', e => e.preventDefault());
    on(canvas, 'wheel', e => { if (!app.ui.hitTest(e.clientX, e.clientY)) this.wheel += Math.sign(e.deltaY); e.preventDefault(); }, { passive: false });
  }

  private fromTouch(): boolean { return performance.now() - this.lastTouch < 1000; }

  /** A text box has the keyboard (chat, trade amount...): keys are for it, not the game. */
  typing(): boolean {
    return focus.current !== null;
  }

  down(code: string): boolean { return !this.typing() && this.held.has(code); }
  pressed(code: string): boolean { return this.pressedNow.has(code); }

  /** Clears the per-frame state (call at the end of each frame). */
  endFrame(): void {
    this.pressedNow.clear();
    this.leftClicked = this.rightClicked = false;
    this.wheel = 0;
  }

  dispose(): void {
    this.off.forEach(f => f());
  }
}
